import React, { useEffect, useMemo, useRef, useState } from 'react';

import { DateTime } from 'luxon';

import styled from '@emotion/styled';

import { FontWeight, SpinLoader } from '@clutter/clean';

import {
  AvailabilityFragment,
  CouponFragment,
  OrderServiceTypeEnum,
  OrderSubtypeEnum,
  OrderTypeEnum,
} from '@graphql/platform';

import { LaborAdjustmentBanner } from '@root/components/checkout/product_pages/steps/shared/appointment/labor_adjustment_banner';
import { StorageCheckoutStepProps } from '@root/components/checkout/types';
import { useTrack } from '@root/initializers/wt';
import { EventSchema } from '@root/resources/wt/types';

import {
  FROM_ISO_OPTIONS,
  hashDateTime,
} from '@shared/calendar/calendar_utils';

import { useStabilizedFunction } from '@utils/hooks';
import { useCalendarCouponTest } from '@utils/hooks/ab_testing';
import { useStorageAvailabilities } from '@utils/hooks/availabilities';
import { useAppointmentFunnelEvents } from '@utils/hooks/funnel_events/use_appointment_time_funnel_events';
import { useTrackFunnelEvents } from '@utils/hooks/funnel_events/use_track_funnel_event';
import { usePricingSetForZIP } from '@utils/hooks/pricing';
import { extractPlan } from '@utils/pricing/pricing_set';
import {
  EventName,
  createThirdPartyConversionEvent,
} from '@utils/third_party_conversion_events';

import chevronDown from '@images/checkout/challenger/chevron_down.svg';

import {
  findMatchingAvailability,
  getScheduledArrivalFee,
  toCheckoutDateScheduled,
  useChallengerFilteredAvailabilities,
} from '../availability';
import { hasChallengerPickupAddress } from '../completion';
import { TABLET_UP as DESKTOP } from '../media';
import { CardPadded, HelpText, Question } from '../shared';
import { CHALLENGER as T } from '../tokens';
import { ChallengerArrivalOptions } from './arrival_options';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const DAY_RATIO = 100 / 7;
const DAY_STEP = { days: 1 } as const;

/**
 * Sunday-first grid covering `viewMonth`, including leading/trailing days
 * from adjacent months (Figma 707:10459).
 */
const buildMonthDays = (viewMonth: DateTime): DateTime[] => {
  const monthStart = viewMonth.startOf('month').startOf('day');
  const monthEnd = viewMonth.endOf('month').startOf('day');
  // Luxon: Mon=1 … Sun=7 → Sunday-first offset
  const leading = monthStart.weekday % 7;
  const trailing = (7 - (monthEnd.weekday % 7)) % 7;
  const gridStart = monthStart.minus({ days: leading });
  const gridEnd = monthEnd.plus({ days: trailing });

  const days: DateTime[] = [];
  for (let date = gridStart; date <= gridEnd; date = date.plus(DAY_STEP)) {
    days.push(date);
  }
  return days;
};

const ScheduleHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: ${T.space[24]};
`;

const CalendarShell = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

/** Funnel-Feedback 1:3261 / 24:1795 — month picker left; selected date in header. */
const CalendarLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: ${T.space[16]} 0;
  border: 1px solid ${T.color.borderDefault};
  border-bottom: none;
  border-radius: ${T.radius.md} ${T.radius.md} 0 0;
  background: ${T.color.bgPrimary};
`;

const CalendarNav = styled.div`
  display: flex;
  align-items: center;
  padding: 0 ${T.space[16]};
  flex-shrink: 0;
`;

/** Figma Month picker — grey pill with prev / label / next. */
const MonthPicker = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 5px 4px;
  border-radius: ${T.radius.sm};
  background: ${T.color.borderLight};
`;

const MonthPickerLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  padding: 0 2px;
  font-weight: 500;
  color: ${T.color.textPrimary};
  white-space: nowrap;
  /* Figma 707:20176 mobile 10/14; desktop 12/16 */
  font-size: 10px;
  line-height: 14px;

  ${DESKTOP} {
    font-size: 12px;
    line-height: 16px;
  }
`;

const MonthPickerButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 6px;
  border: none;
  border-radius: ${T.radius.sm};
  background: transparent;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.35 : 1)};
  outline: none;

  &:focus-visible {
    box-shadow: 0 0 0 2px ${T.color.iconBrand};
  }
`;

const MonthPickerChevron = styled.img<{ direction: 'prev' | 'next' }>`
  display: block;
  width: 10px;
  height: 6px;
  transform: ${({ direction }) =>
    direction === 'prev' ? 'rotate(90deg)' : 'rotate(-90deg)'};
`;

const CalendarBody = styled.div`
  position: relative;
  padding: ${T.space[16]} ${T.space[4]};
  border: 1px solid ${T.color.borderDefault};
  border-radius: 0 0 ${T.radius.md} ${T.radius.md};
  background: ${T.color.bgPrimary};
`;

/** Block day clicks until pickup address is complete; month picker stays interactive. */
const CalendarDayGrid = styled.div<{ $inactive?: boolean }>`
  ${({ $inactive }) =>
    $inactive &&
    `
    pointer-events: none;
  `}
`;

const DayLabels = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: ${T.space[8]};
`;

const DayLabel = styled.div`
  flex-basis: ${DAY_RATIO}%;
  padding: ${T.space[8]} 0;
  text-align: center;
  font-size: 14px;
  color: ${T.color.textTertiary};
  /* Figma 707:20182 mobile Regular 14/20; desktop Medium 14/21 */
  font-weight: 400;
  line-height: 20px;

  ${DESKTOP} {
    font-weight: 500;
    line-height: 21px;
  }
`;

const DayEntries = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  /* Figma 707:10459 / 707:20179 — 8px between week rows */
  row-gap: ${T.space[8]};
`;

const DayEntry = styled.div`
  flex-basis: ${DAY_RATIO}%;
  max-width: ${DAY_RATIO}%;
  padding: 0;
`;

const DayButton = styled.button<{
  $selected: boolean;
  $disabled: boolean;
  /** ZIP entered but pickup address incomplete — grey day numbers per Figma 311:1773. */
  $zipPreview?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
  width: 100%;
  min-height: 48px;
  /* Figma 911:27979 selected uses py-8; available uses py-12 */
  padding: ${({ $selected }) => ($selected ? '8px 0' : '12px 0')};
  box-sizing: border-box;
  border-radius: ${({ $selected }) => ($selected ? T.radius.sm : '10px')};
  /* Figma 911:27979 / 707:10459 — teal selected chip on mobile + desktop */
  border: 1.5px solid
    ${({ $selected }) => ($selected ? T.color.iconBrand : 'transparent')};
  background: ${({ $selected }) =>
    $selected ? T.color.bgSelected : 'transparent'};
  color: ${({ $disabled, $zipPreview }) =>
    $disabled || $zipPreview ? T.color.textDisabled : T.color.textPrimary};
  /* Mobile: available Regular 14/20; selected Medium 13/18 (911:35615) */
  font-size: ${({ $selected }) => ($selected ? '13px' : '14px')};
  font-weight: ${({ $selected }) => ($selected ? 500 : 400)};
  line-height: ${({ $selected }) => ($selected ? '18px' : '20px')};
  text-align: center;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  outline: none;

  &:disabled {
    /* Preserve Figma inactive-day colors; native disabled styling fades the dot. */
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid ${T.color.borderBrand};
    outline-offset: 2px;
  }

  ${DESKTOP} {
    min-height: 49px;
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    color: ${({ $selected, $disabled, $zipPreview }) =>
      $disabled || $zipPreview
        ? T.color.textDisabled
        : $selected
          ? T.color.textSecondary
          : T.color.textPrimary};
  }
`;

/** Figma Funnel-Feedback 311:1816 — 4×4 ellipse, Icon/icon-fill-brand (#08aaa8). */
const AvailabilityDot = styled.span`
  display: block;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: ${T.color.iconBrand};
  flex-shrink: 0;
  opacity: 1;
`;

/**
 * Figma 707:10449 — loading state over the calendar while availability
 * re-fetches after an address ZIP / region change.
 */
const CalendarLoading = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${T.space[12]};
  background: rgba(255, 255, 255, 0.82);
`;

const CalendarLoadingText = styled.div`
  font-family: ${T.font.sans};
  font-size: 14px;
  font-weight: ${FontWeight.Regular};
  line-height: 20px;
  color: ${T.color.textTertiary};
`;

const BookedMessage = styled(HelpText)`
  margin-top: ${T.space[16]};
  text-align: center;
`;

/** Funnel-Feedback 24:1795 — selected date sits in the calendar header. */
const SelectedDateHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${T.space[4]};
  flex: 1 0 0;
  min-width: 0;
  padding-right: ${T.space[16]};
  font-size: 13px;
  line-height: 18px;

  ${DESKTOP} {
    font-size: 14px;
    line-height: 20px;
  }
`;

const SelectedDateLabel = styled.span`
  font-weight: 400;
  color: ${T.color.textTertiary};
`;

const SelectedDateValue = styled.span`
  font-weight: 500;
  color: ${T.color.textTertiary};
  line-height: 18px;

  ${DESKTOP} {
    color: ${T.color.textSecondary};
    line-height: 21px;
  }
`;

type Props = Pick<StorageCheckoutStepProps, 'onChange' | 'enabled' | 'values'>;

/**
 * Challenger schedule UI — calendar card + always-visible arrival card
 * (Figma Desktop 03: “When should we arrive?” / “What time works best…”).
 */
export const ChallengerAppointmentCalendar: React.FC<Props> = ({
  onChange,
  enabled,
  values: { address, commitment, datePreferred, dateScheduled, planSize, zip },
}) => {
  const [viewMonth, setViewMonth] = useState(() =>
    DateTime.local().startOf('month'),
  );
  /** Skip auto-jump after the customer uses next/prev month controls. */
  const userNavigatedMonthRef = useRef(false);
  const pricingSet = usePricingSetForZIP(zip);
  const track = useTrack({ action: 'click' });
  const trackFunnelEvent = useTrackFunnelEvents();
  const addressReady = hasChallengerPickupAddress(address);
  const addressHasCoordinates =
    address?.latitude != null && address?.longitude != null;
  const needsAddressLevelAvailability = addressReady && addressHasCoordinates;
  const calendarInactive = Boolean(zip && !addressReady);

  useEffect(() => {
    if (enabled) {
      trackFunnelEvent({
        schema: EventSchema.WWW__PickupPriceViewed,
        action: 'display',
        metadata: {},
      });
    }
  }, [enabled, trackFunnelEvent]);

  const expectedPlanID = extractPlan(
    commitment,
    planSize,
    pricingSet ?? undefined,
  )?.id;
  const canFetchAvailabilities = Boolean(
    zip && pricingSet && expectedPlanID && commitment && planSize,
  );

  const { availabilities, fetchMore, from, till, loading, fetched } =
    useStorageAvailabilities({
      expectedPlanID,
      orderSubtype: OrderSubtypeEnum.Onboarding,
      orderType: OrderTypeEnum.Pickup,
      serviceType: OrderServiceTypeEnum.FullService,
      zip: zip!,
      // Street-level windows need coordinates. Without them (incognito Maps,
      // restored draft missing lat/lng) keep the ZIP-only query so dates load.
      address: needsAddressLevelAvailability
        ? {
            street: address?.street,
            city: address?.city,
            state: address?.state,
            latitude: address?.latitude,
            longitude: address?.longitude,
          }
        : undefined,
      skip: !canFetchAvailabilities,
      conserveRequests: true,
    });

  // Separate from addressReady: reconciliation/clearing must wait for the
  // street-level availability query, not ZIP-only preview data.
  const addressLevelAvailabilityReady =
    needsAddressLevelAvailability && fetched && !loading;

  const filteredAvailabilities = useChallengerFilteredAvailabilities(
    addressReady ? availabilities : [],
    datePreferred,
  );
  const scheduledFeePreview = useMemo(
    () => getScheduledArrivalFee(availabilities),
    [availabilities],
  );

  // Derive bookable days from the current availability payload.
  // ZIP-only (no street address) is a grey preview; a complete address refetches
  // windows for that address and replaces the ZIP markers.
  const { availableDays, couponDays, firstAvailable } = useMemo(() => {
    const selectables = new Set<string>();
    const couponSelectables: Record<string, CouponFragment> = {};
    let first: DateTime | undefined;
    for (const availability of availabilities) {
      const selectable = DateTime.fromISO(
        availability.fromTime,
        FROM_ISO_OPTIONS,
      );
      if (!selectable.isValid) continue;
      const dayKey = hashDateTime(selectable);
      selectables.add(dayKey);
      if (!first || selectable < first) first = selectable;
      if (availability.coupon) {
        couponSelectables[dayKey] = availability.coupon;
      }
    }
    return {
      availableDays: selectables,
      couponDays: couponSelectables,
      firstAvailable: first,
    };
  }, [availabilities]);

  // Reset month when ZIP / address readiness changes; day markers follow above.
  useEffect(() => {
    userNavigatedMonthRef.current = false;
    setViewMonth(DateTime.local().startOf('month'));
  }, [zip, addressReady]);

  // Keep availability pages loaded through the visible month (2-week pages).
  const viewMonthKey = viewMonth.toFormat('yyyy-MM');
  const tillKey = till.toISODate();
  useEffect(() => {
    if (!canFetchAvailabilities || loading || !fetched) return;
    if (viewMonth.endOf('month') < till.startOf('day')) return;
    fetchMore();
    // Depend on month/till keys so an unstable fetchMore identity cannot loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [canFetchAvailabilities, viewMonthKey, tillKey, loading, fetched]);

  // If the visible month has no loaded bookable days (MockDate re-render, ZIP
  // change, fully booked current month), jump to the first availability month.
  // Do not override after the customer uses the month picker.
  useEffect(() => {
    if (!zip || !fetched || !firstAvailable || userNavigatedMonthRef.current) {
      return;
    }

    const monthHasAvailability = Array.from(availableDays).some((key) => {
      const [year, month] = key.split('-').map(Number);
      return year === viewMonth.year && month === viewMonth.month;
    });
    if (monthHasAvailability) return;

    setViewMonth(
      DateTime.local(firstAvailable.year, firstAvailable.month, 1).startOf(
        'month',
      ),
    );
  }, [zip, fetched, firstAvailable, availableDays, viewMonth]);

  useEffect(() => {
    if (!addressReady || !addressLevelAvailabilityReady) return;
    const selectedDayUnavailable =
      !!datePreferred && !availableDays.has(hashDateTime(datePreferred));
    const noAddressAvailability = availableDays.size === 0;
    if (!selectedDayUnavailable && !(noAddressAvailability && dateScheduled)) {
      return;
    }
    if (datePreferred) onChange('datePreferred', undefined);
    if (dateScheduled) onChange('dateScheduled', undefined);
  }, [
    addressLevelAvailabilityReady,
    addressReady,
    availableDays,
    datePreferred,
    dateScheduled,
    onChange,
  ]);

  // After resume, draft/session ISO strings may differ from live API windows.
  useEffect(() => {
    if (!datePreferred || !dateScheduled || !addressLevelAvailabilityReady) {
      return;
    }
    const windows = filteredAvailabilities ?? [];
    if (!windows.length) {
      onChange('dateScheduled', undefined);
      return;
    }

    const match = findMatchingAvailability(dateScheduled, windows);
    if (!match) {
      onChange('dateScheduled', undefined);
      return;
    }
    if (
      match.fromTime !== dateScheduled.fromTime ||
      match.tillTime !== dateScheduled.tillTime ||
      match.formatted !== dateScheduled.formatted ||
      match.duration !== dateScheduled.duration ||
      match.feeAmount?.value !== dateScheduled.feeAmount?.value
    ) {
      onChange('dateScheduled', toCheckoutDateScheduled(match));
    }
  }, [
    datePreferred,
    dateScheduled,
    addressLevelAvailabilityReady,
    filteredAvailabilities,
    onChange,
  ]);

  const earliestMonth = from.startOf('month');
  const canGoPrevMonth = viewMonth > earliestMonth;
  const calendarLoading = Boolean(
    zip && canFetchAvailabilities && loading && !fetched,
  );
  const monthNavLocked = calendarLoading;
  const availabilityFetched = Boolean(fetched && zip);

  const currentDay = DateTime.local().startOf('day');

  const isUnavailable = useStabilizedFunction(
    (value: DateTime, availabilitySet: Set<string> | undefined) =>
      !availabilitySet?.has(hashDateTime(value)),
  );

  const calendarCouponEligible = useCalendarCouponTest();

  const getCoupon = useStabilizedFunction(
    (
      value: DateTime,
      availabilitySet: Record<string, CouponFragment> | undefined,
    ) =>
      calendarCouponEligible
        ? availabilitySet?.[hashDateTime(value)]
        : undefined,
  );

  const { trackAppointmentDateCompleted, trackArrivalWindowCompleted } =
    useAppointmentFunnelEvents({
      enabled,
      from,
      till,
      originZip: zip ?? '',
      appointmentDatesShown: availabilityFetched,
      arrivalWindowCount: filteredAvailabilities?.length,
      currentDay,
      availableDays,
      isUnavailable,
    });

  const onTimeChange = (
    availability: AvailabilityFragment | undefined,
    _0: boolean,
  ) => {
    if (!availability) {
      onChange('dateScheduled', undefined);
      return;
    }
    trackArrivalWindowCompleted(availability.formatted);
    onChange('dateScheduled', availability);
    createThirdPartyConversionEvent(EventName.AVAILABILITY, {
      date_scheduled: availability.fromTime,
    });
  };

  const selectedAvailability = useMemo(() => {
    if (!datePreferred) return;
    return filteredAvailabilities?.find((a) =>
      datePreferred.hasSame(DateTime.fromISO(a.fromTime), 'day'),
    );
  }, [datePreferred, filteredAvailabilities]);

  const isPastDay = (value: DateTime) => value <= currentDay;

  const monthDays = useMemo(() => buildMonthDays(viewMonth), [viewMonth]);

  const onDateChange = (date: DateTime) => {
    if (!availableDays.has(hashDateTime(date))) return;
    if (calendarInactive) return;

    track({
      objectName: 'appointment_date',
      objectType: 'button',
      value: date.toISO(),
    });
    trackFunnelEvent({
      schema: EventSchema.WWW__PickupPriceCompleted,
      action: 'click',
      metadata: {},
    });
    if (calendarCouponEligible) {
      onChange('coupon', getCoupon(date, couponDays));
    }
    trackAppointmentDateCompleted(date.toISO());
    onChange('datePreferred', date);
    // Clear any prior arrival choice — customer must pick Flexible or Scheduled.
    onChange('dateScheduled', undefined);
    createThirdPartyConversionEvent(EventName.DATE_PREFERRED, {
      date_preferred: date?.toISO(),
      zip_code: zip,
    });
  };

  return (
    <>
      <CardPadded>
        <ScheduleHeader>
          <Question>When should we pickup your items?</Question>
          <HelpText>
            Schedule your pickup today. If your plans change, you can update
            your pickup through your Client portal.
          </HelpText>
        </ScheduleHeader>

        <CalendarShell>
          {/* Funnel-Feedback 1:3261 / 24:1795 — picker left; selected date in header. */}
          <CalendarLabel>
            <CalendarNav>
              <MonthPicker>
                <MonthPickerButton
                  type="button"
                  aria-label="Previous month"
                  disabled={!canGoPrevMonth || monthNavLocked}
                  onClick={() => {
                    track({
                      objectName: 'calendar_month',
                      objectType: 'button',
                      label: 'Previous month',
                    });
                    userNavigatedMonthRef.current = true;
                    setViewMonth((month) => month.minus({ months: 1 }));
                  }}
                >
                  <MonthPickerChevron
                    src={chevronDown}
                    alt=""
                    direction="prev"
                  />
                </MonthPickerButton>
                <MonthPickerLabel>
                  {viewMonth.toFormat('MMMM yyyy')}
                </MonthPickerLabel>
                <MonthPickerButton
                  type="button"
                  aria-label="Next month"
                  disabled={monthNavLocked}
                  onClick={() => {
                    track({
                      objectName: 'calendar_month',
                      objectType: 'button',
                      label: 'Next month',
                    });
                    userNavigatedMonthRef.current = true;
                    setViewMonth((month) => month.plus({ months: 1 }));
                  }}
                >
                  <MonthPickerChevron
                    src={chevronDown}
                    alt=""
                    direction="next"
                  />
                </MonthPickerButton>
              </MonthPicker>
            </CalendarNav>
            {datePreferred && (
              <SelectedDateHeader>
                <SelectedDateLabel>Selected date:</SelectedDateLabel>
                <SelectedDateValue>
                  {datePreferred.toFormat('ccc, MMMM d, yyyy')}
                </SelectedDateValue>
              </SelectedDateHeader>
            )}
          </CalendarLabel>
          <CalendarBody>
            <CalendarDayGrid $inactive={calendarInactive}>
              <DayLabels>
                {DAY_LABELS.map((label) => (
                  <DayLabel key={label}>{label}</DayLabel>
                ))}
              </DayLabels>
              <DayEntries>
                {monthDays.map((date) => {
                  const outsideMonth = !date.hasSame(viewMonth, 'month');
                  const hasAvailability = availableDays.has(hashDateTime(date));
                  const dayPast =
                    outsideMonth || (isPastDay(date) && !hasAvailability);
                  const dayUnavailable =
                    outsideMonth ||
                    (loading && !fetched) ||
                    isUnavailable(date, availableDays);
                  const previewLocked =
                    calendarInactive &&
                    hasAvailability &&
                    !outsideMonth &&
                    !dayPast;
                  const zipPreviewDay =
                    calendarInactive && !outsideMonth && !dayPast;
                  const isDisabled = dayPast || dayUnavailable;
                  const showAvailabilityDot =
                    hasAvailability && !outsideMonth && !dayPast;
                  const isSelected =
                    !outsideMonth &&
                    !!datePreferred &&
                    datePreferred.hasSame(date, 'day');

                  return (
                    <DayEntry key={String(date)}>
                      <DayButton
                        type="button"
                        disabled={isDisabled && !previewLocked}
                        aria-pressed={isSelected}
                        $selected={isSelected}
                        $disabled={isDisabled || zipPreviewDay}
                        $zipPreview={zipPreviewDay}
                        data-test-id={`${date.toISO()}-date`}
                        onClick={() => {
                          if (
                            loading ||
                            calendarInactive ||
                            isDisabled ||
                            previewLocked
                          ) {
                            return;
                          }
                          onDateChange(date);
                        }}
                      >
                        {date.day}
                        {showAvailabilityDot && <AvailabilityDot aria-hidden />}
                      </DayButton>
                    </DayEntry>
                  );
                })}
              </DayEntries>
            </CalendarDayGrid>
            {calendarLoading && (
              <CalendarLoading aria-live="polite" aria-busy="true">
                <SpinLoader />
                <CalendarLoadingText>
                  Updating availability…
                </CalendarLoadingText>
              </CalendarLoading>
            )}
          </CalendarBody>
        </CalendarShell>

        {canFetchAvailabilities &&
          fetched &&
          !loading &&
          availableDays.size === 0 && (
            <BookedMessage>
              No pickup dates are available for this ZIP right now. Please try a
              different address or check back later.
            </BookedMessage>
          )}

        {datePreferred && !filteredAvailabilities?.length && (
          <BookedMessage>
            We’re fully booked for that date! Please try another date.
          </BookedMessage>
        )}

        {datePreferred &&
          !!filteredAvailabilities?.length &&
          calendarCouponEligible &&
          selectedAvailability?.coupon && (
            <div style={{ marginTop: T.space[16] }}>
              <LaborAdjustmentBanner
                date={datePreferred}
                coupon={selectedAvailability.coupon}
              />
            </div>
          )}
      </CardPadded>

      <ChallengerArrivalOptions
        // Remount when the day changes so arrival UI never keeps a prior choice.
        key={datePreferred?.toISODate() ?? 'no-date'}
        selectedAvailability={dateScheduled}
        availabilities={filteredAvailabilities ?? []}
        scheduledFeePreview={scheduledFeePreview}
        dateSelected={!!datePreferred}
        onChange={onTimeChange}
      />
    </>
  );
};
